import { Control, Controller, FieldErrors, UseFormReturn } from 'react-hook-form'
import { FormSelect } from '../../../../components/forms/select/FormSelect'
import { FormSelectAutocomplete } from '../../../../components/forms/select/FormSelectAutocomplete'
import {
  OpcionSitiosSelect,
  OpcionUsuarioSelect,
} from '../../../../components/forms/select/OpcionesSelect'
import {
  useLazyRecuperarContactosSitioQuery,
  useLazyRecuperarSitioAutocompleteQuery,
} from '../../../../redux/api'
import { ContactoSitio, Sitio } from '../../../sitios/models'
import { TAutorizacionForm } from '../../models/autorizacionForm.type'

type Props = {
  control: Control<TAutorizacionForm>
  errors: FieldErrors<TAutorizacionForm>
  setContactos: (
    arg: {
      etiqueta: string
      valor?: string
      correo: string
      descPosicion: string
    }[]
  ) => void
  contactos: any
  formPagina: UseFormReturn<TAutorizacionForm>
  usuarioPermiso?: boolean
}
export const SitioForm = ({
  control,
  errors,
  setContactos,
  contactos,
  formPagina: { getValues, setValue },
  usuarioPermiso,
}: Props) => {
  // Seleccion de sitio + responsable.
  // Contrato UI:
  // - Sitio: autocomplete (consulta a partir de 2 caracteres)
  // - Contacto: depende del sitio; se carga por API y se limpia al cambiar el sitio
  const [getSitios] = useLazyRecuperarSitioAutocompleteQuery()
  const [recuperarContactos] = useLazyRecuperarContactosSitioQuery()

  /**
   * @description Agrega las opciones al select de autocompletado
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 07/12/2022
   * @param entradaBusqueda
   * @param callback Regresa el arreglo de opciones del select
   */
  const agregaOpcionesSelect = (entradaBusqueda: string, callback: Function) => {
    // Evita saturar backend; solo consulta con longitud minima.
    if (entradaBusqueda && entradaBusqueda.length >= 2) {
      getSitios(entradaBusqueda)
        .unwrap()
        .then((resp) => {
          callback(
            resp?.map((sitio: Sitio) => ({
              etiqueta: sitio.nombre,
              valor: sitio.idSitio,
              descRazonSocial: sitio.descRazonSocial,
              razonSocial: sitio.razonSocial,
              rfc: sitio.rfc,
            }))
          )
        })
        .catch(console.error)
    }
  }

  /**
   * @description Recupera los contactos relacionados al sitio por API,
   * con información del select del sitio para
   * generar las opciones en el select de contactos.
   *
   * @author ana.carrasco Hábil MX
   * @version 1.0.0, 13/01/2023
   * @param idSitio id del sitio seleccionado en el select del sitio
   */
  const recuperarContactosPorIdSitio = (idSitio: {
    etiqueta?: string
    valor: string
    descRazonSocial?: string
    razonSocial?: string
    rfc?: string
  }) => {
    // Carga contactos y transforma al formato de opcion requerido por FormSelect.
    if (idSitio) {
      recuperarContactos(idSitio.valor)
        .unwrap()
        .then((resp: ContactoSitio[]) => {
          const contactos = resp.map((contacto: ContactoSitio) => ({
            etiqueta: ` ${contacto.nombre}  ${contacto.apellidoPaterno}  ${contacto.apellidoMaterno}`,
            valor: contacto.idContacto,
            correo: contacto.correo,
            descPosicion: contacto.descPosicion,
          }))
          setContactos(contactos)
        })
        .catch(console.error)
    }
  }

  return (
    <div className={`${usuarioPermiso ? '' : 'overlay overlay-allowed'} ms-20 d-flex row m-0`}>
      <div className='d-flex'>
        <div className='col-2 '>
          <h3 className='me-15'>Sitio</h3>
        </div>
        <div className='col-10'>
          <div className='d-flex'>
            <div className='col-5'>
              <Controller
                control={control}
                name='idSitio'
                render={({ field: { onChange, value } }) => (
                  <FormSelectAutocomplete
                    isDisabled={!usuarioPermiso}
                    isObligatorio={true}
                    value={value}
                    cargaOpciones={agregaOpcionesSelect}
                    nombre='Sitio'
                    onChange={(e: {
                      etiqueta?: string
                      valor: string
                      descRazonSocial?: string
                      razonSocial?: string
                      rfc?: string
                    }) => {
                      onChange(e)
                      recuperarContactosPorIdSitio(e)
                      // Evita responsable "colgado" de un sitio anterior.
                      setValue('idContactoSitio', null)
                    }}
                    placeholder='Ej. Chihuaha'
                    Componente={OpcionSitiosSelect}
                    errors={errors}
                  />
                )}
              />
              {errors && errors['idSitio'] && (
                <p className='text-danger text-end fs-5'>{errors['idSitio']?.message}</p>
              )}
            </div>

            <div className='col-5 mx-3'>
              <Controller
                control={control}
                name='idContactoSitio'
                render={({ field: { onChange, value } }) => (
                  <FormSelect
                    isDisabled={!getValues('idSitio') || !usuarioPermiso}
                    isObligatorio={true}
                    value={value}
                    nombreCampo='idContactoSitio'
                    nombre='Responsable del sitio'
                    elementos={contactos}
                    onChange={(e: {
                      etiqueta: string
                      valor: string
                      correo?: string
                      descPosicion?: string
                    }) => {
                      onChange(e)
                    }}
                    placeholder={
                      getValues('idSitio')
                        ? 'Selecciona un contacto'
                        : 'Selecciona un sitio primero'
                    }
                    Componente={OpcionUsuarioSelect}
                  />
                )}
              />
              {errors && errors['idContactoSitio'] && (
                <p className='text-danger text-end fs-5'>{errors['idContactoSitio']?.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
